/**
 * @file `ComposePanel` — root of the `/emails` route: one compose flow whose
 *       top-level axis is the send-type (Marketing | Transactional), split into
 *       two in-panel tabs (Audience / Delivery + Message).
 * @module @labcraft/admin/ui/emails/compose-panel
 *
 * #1619 retired the Individual | Campaign mode split. Since #1616 gave every
 * send an explicit transactional-vs-marketing lane decoupled from audience
 * size, a 1:1 "individual" send is just a size-1 transactional broadcast — so
 * there is one component now:
 *
 *   - The **send-type** is the top-level segment (Marketing | Transactional).
 *     Marketing → Resend from `@news.labcraft.dev`, unsubscribe footer,
 *     auto-suppression. Transactional → Workspace SMTP relay from
 *     `@labcraft.dev`, no footer, sent without regard for unsubscription.
 *   - Two **tabs** keep the body off a vertical scroll: **Audience** (who —
 *     filters + a candidates multi-select scoped to "everyone matching" or the
 *     checked rows) and **Delivery + Message** (the From / Reply-To identity +
 *     the subject/body composer). The header (title, send-type, Review send)
 *     persists across both.
 *
 * The Learners deep-link `/emails?recipient=<id>` lands in Transactional with
 * that learner pre-selected. The review drawer wraps every send; data flows
 * through the hook layer — components never call `useQuery` / `useMutation`
 * directly.
 */

import { useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { useSearchParams } from 'react-router'
import type { LearnerStatus } from '../../shared/routes.ts'
import type {
  EmailRecipientFilters,
  SenderIdentity,
} from '../../shared/emails_routes.ts'
import {
  useAllTags,
  useDebounce,
  useEmailCandidates,
  useEmailFrom,
  useEmailSenders,
  useRecipientCount,
  useSendBroadcastMutation,
} from '../hooks/index.ts'
import { StatusPill } from '../learners/status_pill.tsx'
import { TagMultiSelect } from '../learners/tag_filter.tsx'
import { describeFilters } from './describe_filters.ts'
import { MessageComposer, type ComposerTemplate } from './message_composer.tsx'
import { renderMarkdown } from './render_markdown.ts'
import { ReviewDrawer } from './review_drawer.tsx'

type Format = 'text' | 'html'
type StatusFilter = LearnerStatus | 'all'
type ProgressFilter = 'any' | 'yes' | 'no'
type Scope = 'all' | 'selected'
/** Which of the two compose tabs is showing. */
type Tab = 'audience' | 'message'
/** Send-type lane (#1616): transactional (@labcraft.dev, no footer) vs marketing. */
type Classification = 'transactional' | 'marketing'

/** A send is "broad" (warrants extra care) past this recipient count. */
const BROAD_MIN = 25

/** Placeholder-fill starter templates offered in HTML compose. */
const COMPOSE_TEMPLATES: ReadonlyArray<ComposerTemplate> = [
  {
    id: 'announcement',
    label: 'Announcement',
    md: '# [Headline]\n\nHi there,\n\n[What&rsquo;s new — one or two sentences.]\n\n- [Point one]\n- [Point two]\n\n[Read more →](https://labcraft.dev)\n\n— The LabCraft team',
  },
  {
    id: 'reengagement',
    label: 'Re-engagement',
    md: '# We saved your spot\n\nHi there,\n\nIt&rsquo;s been a while — [one reason to come back].\n\n[Pick up where you left off →](https://labcraft.dev)\n\n— The LabCraft team',
  },
  {
    id: 'letter',
    label: 'Plain letter',
    md: 'Hi there,\n\n[Write your message here.]\n\n— [Your name]',
  },
]

const CATEGORIES = ['campaign', 'organic', 'referral', 'direct'] as const
type Category = (typeof CATEGORIES)[number]

const STATUSES: ReadonlyArray<{ id: StatusFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'banned', label: 'Banned' },
]

/** `YYYY-MM-DD` → start-of-day ISO; empty → undefined. */
function dayStartIso(day: string): string | undefined {
  return day ? `${day}T00:00:00.000Z` : undefined
}

/** `YYYY-MM-DD` → end-of-day ISO; empty → undefined. */
function dayEndIso(day: string): string | undefined {
  return day ? `${day}T23:59:59.999Z` : undefined
}

/** `2026-05-21T…` → `May 21, 2026`; invalid → em dash. */
function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Bare address from a mailbox (`Label <a@b>`) or a bare address, lowercased. */
function parseAddress(v: string): string {
  const m = v.match(/<([^>]+)>/)
  return (m ? m[1]! : v).trim().toLowerCase()
}

/** The Marketing | Transactional send-type segment (the top-level axis, #1619). */
function SendTypeSeg(props: {
  value: Classification
  onValue: (c: Classification) => void
}): JSX.Element {
  return (
    <div className="seg" role="group" aria-label="Send type">
      {(['marketing', 'transactional'] as const).map(c => (
        <button
          key={c}
          type="button"
          className="seg__btn"
          aria-pressed={props.value === c}
          onClick={() => props.onValue(c)}>
          {c === 'marketing' ? 'Marketing' : 'Transactional'}
        </button>
      ))}
    </div>
  )
}

/**
 * The per-send From picker (allowed identities for the active lane) + the
 * Reply-To field (#1616). `from === ''` selects the lane's default From; the
 * server re-validates any chosen `from` against the lane's allowlist. The lane
 * default is de-duped out of the identity list (it already IS the first
 * option), comparing on the parsed address so it matches whether the identity
 * is bare (`noreply@labcraft.dev`) or mailbox form
 * (`LabCraft <noreply@labcraft.dev>`). Each option shows its From value
 * verbatim — no "Default —" prefix, no display-name duplication.
 *
 * @property identities - Selectable identities for the active lane.
 * @property from - Selected From address, or `''` for the lane default.
 * @property onFrom - Update the selected From.
 * @property replyTo - The Reply-To override (empty ⇒ the default).
 * @property onReplyTo - Update the Reply-To.
 * @property defaultFrom - The lane's default From, shown on the default option.
 * @property defaultReplyTo - The default Reply-To, shown as the placeholder.
 */
function SenderFields(props: {
  identities: SenderIdentity[]
  from: string
  onFrom: (v: string) => void
  replyTo: string
  onReplyTo: (v: string) => void
  defaultFrom?: string
  defaultReplyTo?: string
}): JSX.Element {
  const defaultAddr = props.defaultFrom ? parseAddress(props.defaultFrom) : null
  const identities = props.identities.filter(
    s => parseAddress(s.address) !== defaultAddr,
  )
  return (
    <div className="sender-fields">
      <label className="field">
        <span className="field-label">From</span>
        <select
          className="lc-input"
          value={props.from}
          onChange={e => props.onFrom(e.target.value)}>
          <option value="">{props.defaultFrom || 'Default'}</option>
          {identities.map(s => (
            <option key={s.address} value={s.address}>
              {s.address}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field-label">Reply-To</span>
        <input
          className="lc-input"
          type="text"
          placeholder={props.defaultReplyTo || 'replies go to the From address'}
          value={props.replyTo}
          onChange={e => props.onReplyTo(e.target.value)}
        />
      </label>
    </div>
  )
}

/** Render the unified compose route. */
export function ComposePanel(): JSX.Element {
  const [searchParams] = useSearchParams()
  const deepLinkId = searchParams.get('recipient')

  // Top-level axis: a Learners deep-link is a 1:1 transactional send.
  const [classification, setClassification] = useState<Classification>(
    deepLinkId ? 'transactional' : 'marketing',
  )
  const [tab, setTab] = useState<Tab>('audience')

  // Filters (Timezone dropped in #1619).
  const [status, setStatus] = useState<StatusFilter>('all')
  const [q, setQ] = useState('')
  const [after, setAfter] = useState('')
  const [before, setBefore] = useState('')
  const [source, setSource] = useState('')
  const [category, setCategory] = useState<Category | ''>('')
  const [progress, setProgress] = useState<ProgressFilter>('any')
  // Tag include/exclude targeting (#1639). Raw strings; the account daemon
  // canonicalizes. These ride the resolve + send filter set verbatim so a
  // tag-scoped send stays tag-scoped.
  const [includeTags, setIncludeTags] = useState<string[]>([])
  const [excludeTags, setExcludeTags] = useState<string[]>([])

  // Scope + selection — seeded from the deep-link so that learner is checked.
  const [scope, setScope] = useState<Scope>(deepLinkId ? 'selected' : 'all')
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    deepLinkId ? new Set([deepLinkId]) : new Set(),
  )

  // Message.
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [format, setFormat] = useState<Format>('text')

  // Send identity (#1616): the per-send From / Reply-To.
  const [from, setFrom] = useState('')
  const [replyTo, setReplyTo] = useState('')
  // A lane switch invalidates a From picked from the other lane's list.
  useEffect(() => {
    setFrom('')
  }, [classification])

  const dq = useDebounce(q, 250)
  const dsrc = useDebounce(source, 250)

  const filters = useMemo<EmailRecipientFilters>(() => {
    const f: EmailRecipientFilters = {}
    if (status !== 'all') f.status = status
    if (dq.trim()) f.q = dq.trim()
    const a = dayStartIso(after)
    if (a) f.signedUpAfter = a
    const b = dayEndIso(before)
    if (b) f.signedUpBefore = b
    if (dsrc.trim()) f.acquisitionSource = dsrc.trim()
    if (category) f.acquisitionCategory = category
    if (progress !== 'any') f.hasProgress = progress === 'yes'
    if (includeTags.length > 0) f.tags = includeTags
    if (excludeTags.length > 0) f.tagsNot = excludeTags
    return f
  }, [
    status,
    dq,
    after,
    before,
    dsrc,
    category,
    progress,
    includeTags,
    excludeTags,
  ])

  // A filter change re-scopes the shown audience, so any prior hand-picks are
  // stale against it. Clear them and fall back to "all" (F1) — but never on the
  // initial mount, which would wipe the deep-link seed (compare-prev guards the
  // mount + React strict-mode's double-invoke).
  const prevFilters = useRef<EmailRecipientFilters | null>(null)
  useEffect(() => {
    if (prevFilters.current !== null && prevFilters.current !== filters) {
      setSelected(new Set())
      setScope('all')
    }
    prevFilters.current = filters
  }, [filters])

  const count = useRecipientCount(filters)
  const candidates = useEmailCandidates(filters)
  const allTags = useAllTags()
  const campaignFrom = useEmailFrom('marketing')
  const smtpFrom = useEmailFrom('transactional')
  const senders = useEmailSenders()
  const send = useSendBroadcastMutation()
  const rows = candidates.data?.learners ?? null

  // Lane-scoped identity picker + preview From (#1616): the default From and the
  // selectable identities follow the send-type lane.
  const laneDefaultFrom =
    classification === 'marketing' ? campaignFrom : smtpFrom
  const laneIdentities = (senders.data?.senders ?? []).filter(
    s => s.classification === classification,
  )
  const previewFrom = from || laneDefaultFrom

  const toggle = (id: string): void => {
    setScope('selected')
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const [reviewOpen, setReviewOpen] = useState(false)
  const [ack, setAck] = useState(false)

  const resolvedCount = count.data?.count ?? null
  const recipientCount = scope === 'all' ? resolvedCount : selected.size
  const isBroad = scope === 'all' || (recipientCount ?? 0) > BROAD_MIN
  const hasMsg = subject.trim().length > 0 && body.trim().length > 0
  const canReview =
    hasMsg && (scope === 'all' ? (resolvedCount ?? 0) > 0 : selected.size > 0)
  const marketing = classification === 'marketing'
  // A deep-linked recipient is targeted by id regardless; only warn when they
  // aren't visible in the loaded candidates (a large / filtered roster) so the
  // operator knows the pre-selection is real even though no row shows checked.
  const deepLinkVisible =
    deepLinkId !== null && (rows?.some(l => l.id === deepLinkId) ?? false)
  const deepLinkMissed =
    deepLinkId !== null &&
    selected.has(deepLinkId) &&
    !deepLinkVisible &&
    !candidates.isPending
  const hint =
    (recipientCount ?? 0) === 0
      ? 'Build an audience with filters.'
      : !hasMsg
        ? 'Add a subject and message.'
        : marketing
          ? 'Marketing send · auto-suppressed · unsubscribe footer.'
          : 'Transactional send · @labcraft.dev · no footer · sent regardless of unsubscribes.'

  const openReview = (): void => {
    if (!canReview) return
    send.reset()
    setAck(false)
    setReviewOpen(true)
  }

  const onSend = (): void => {
    send.mutate({
      subject: subject.trim(),
      body: format === 'html' ? renderMarkdown(body) : body,
      format,
      filters,
      classification,
      ...(from ? { from } : {}),
      ...(replyTo.trim() ? { replyTo: replyTo.trim() } : {}),
      ...(scope === 'selected' ? { recipientIds: [...selected] } : {}),
    })
  }

  const composeAnother = (): void => {
    setReviewOpen(false)
    setSubject('')
    setBody('')
    setFormat('text')
    setAck(false)
    setFrom('')
    setReplyTo('')
    send.reset()
  }

  const countError = count.isError
    ? count.error.message || 'Could not resolve the recipient count.'
    : null
  const candidatesError = candidates.isError
    ? candidates.error.message || 'Could not load candidate learners.'
    : null
  const sendError = send.isError ? send.error.message || 'Send failed.' : null

  const audienceBadge =
    scope === 'selected'
      ? String(selected.size)
      : count.isPending
        ? '…'
        : resolvedCount !== null
          ? String(resolvedCount)
          : '—'

  return (
    <div className="cmp">
      <header className="panel-head">
        <div>
          <div className="panel-head__title-row">
            <h1 className="panel-head__title">Compose</h1>
            <SendTypeSeg value={classification} onValue={setClassification} />
          </div>
          <p className="panel-head__sub">
            {marketing
              ? '// broadcast to a filtered audience. suppressions excluded.'
              : '// direct mail to a filtered audience. sent regardless of unsubscribes.'}
          </p>
        </div>
        <div className="cmp-head-action">
          <button
            type="button"
            className={
              isBroad
                ? 'lc-btn lc-btn--primary cmp-send--broad'
                : 'lc-btn lc-btn--primary'
            }
            disabled={!canReview}
            onClick={openReview}>
            Review send
          </button>
          <span className="cmp-foot__hint">{hint}</span>
        </div>
      </header>

      <div className="cmp-tabs" role="tablist" aria-label="Compose steps">
        <button
          type="button"
          role="tab"
          className="cmp-tab"
          aria-selected={tab === 'audience'}
          onClick={() => setTab('audience')}>
          Audience <span className="cmp-tab__badge">{audienceBadge}</span>
        </button>
        <button
          type="button"
          role="tab"
          className="cmp-tab"
          aria-selected={tab === 'message'}
          onClick={() => setTab('message')}>
          Delivery + Message
        </button>
      </div>

      {countError !== null && (
        <div className="panel-banner panel-banner--error">
          <b>Recipient count unavailable.</b> {countError}
        </div>
      )}
      {deepLinkMissed && (
        <div className="panel-banner panel-banner--warn">
          <b>Recipient carried over from Learners.</b> We couldn’t preload their
          details in this tab — they’re still targeted; search below to confirm.
        </div>
      )}

      {tab === 'audience' ? (
        <div className="cmp-tabpanel">
          <section className="compose-section cmp-tabbody">
            <div className="compose-h">
              <h2 className="compose-h__t">Audience</h2>
              <span className="compose-h__hint">filter the learner base</span>
            </div>

            <div className="filters-line">
              <label className="field">
                <span className="field-label">Status</span>
                <select
                  className="lc-input"
                  value={status}
                  onChange={e => setStatus(e.target.value as StatusFilter)}>
                  {STATUSES.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field-label">Search</span>
                <input
                  className="lc-input"
                  type="search"
                  placeholder="email or name…"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Signed up after</span>
                <input
                  className="lc-input"
                  type="date"
                  value={after}
                  onChange={e => setAfter(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Signed up before</span>
                <input
                  className="lc-input"
                  type="date"
                  value={before}
                  onChange={e => setBefore(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Acquisition source</span>
                <input
                  className="lc-input"
                  type="text"
                  placeholder="e.g. hackernews"
                  value={source}
                  onChange={e => setSource(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Acquisition category</span>
                <select
                  className="lc-input"
                  value={category}
                  onChange={e => setCategory(e.target.value as Category | '')}>
                  <option value="">Any</option>
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field-label">Progress</span>
                <select
                  className="lc-input"
                  value={progress}
                  onChange={e => setProgress(e.target.value as ProgressFilter)}>
                  <option value="any">Any</option>
                  <option value="yes">Started a course</option>
                  <option value="no">Never started</option>
                </select>
              </label>
              <TagMultiSelect
                label="Tagged"
                selected={includeTags}
                onChange={setIncludeTags}
                suggestions={allTags.data ?? []}
                datalistId="lc-compose-tags-include"
                placeholder="tag…"
              />
              <TagMultiSelect
                label="Not tagged"
                selected={excludeTags}
                onChange={setExcludeTags}
                suggestions={allTags.data ?? []}
                datalistId="lc-compose-tags-exclude"
                placeholder="tag…"
              />
            </div>

            <div
              className="scope-radios"
              role="group"
              aria-label="Recipient scope">
              <label>
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'all'}
                  onChange={() => setScope('all')}
                />
                <span>
                  Everyone matching{' '}
                  {count.isPending ? (
                    <span className="spin" />
                  ) : resolvedCount !== null ? (
                    <b>{resolvedCount}</b>
                  ) : (
                    <span className="cell-bad">unavailable</span>
                  )}
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'selected'}
                  onChange={() => setScope('selected')}
                />
                <span>Only checked rows ({selected.size})</span>
              </label>
            </div>

            {candidatesError !== null && (
              <div className="panel-banner panel-banner--error">
                <b>Could not load candidates.</b> {candidatesError}
              </div>
            )}

            <div className="candidates">
              <table className="table">
                <thead>
                  <tr>
                    <th className="checkcell" aria-label="Select" />
                    <th>Email</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Signed up</th>
                    <th>Timezone</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.isPending && rows === null && (
                    <tr>
                      <td className="empty-row" colSpan={6}>
                        // loading candidates…
                      </td>
                    </tr>
                  )}
                  {!candidates.isPending && rows && rows.length === 0 && (
                    <tr>
                      <td className="empty-row" colSpan={6}>
                        // no learners match this filter.
                      </td>
                    </tr>
                  )}
                  {rows?.map(l => (
                    <tr
                      key={l.id}
                      className="is-clickable"
                      aria-selected={selected.has(l.id)}
                      onClick={() => toggle(l.id)}>
                      <td className="checkcell">
                        <input
                          type="checkbox"
                          aria-label={`Select ${l.email}`}
                          checked={selected.has(l.id)}
                          onChange={() => toggle(l.id)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td className="cell-email">{l.email}</td>
                      <td
                        className={
                          l.displayName
                            ? 'cell-name'
                            : 'cell-name cell-name--empty'
                        }>
                        {l.displayName || '—'}
                      </td>
                      <td>
                        <StatusPill status={l.status} />
                      </td>
                      <td className="cell-meta">{formatDate(l.createdAt)}</td>
                      <td className="cell-meta">{l.timezone || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows &&
                candidates.data &&
                candidates.data.total > candidates.data.learners.length &&
                scope === 'all' && (
                  <p className="candidates__more">
                    // preview page. &ldquo;Everyone matching&rdquo; sends to
                    all {resolvedCount ?? '…'}.
                  </p>
                )}
            </div>
          </section>
        </div>
      ) : (
        <div className="cmp-tabpanel">
          <section className="cmp-tabbody">
            <div className="send-identity">
              <div className="send-identity__head">
                <div className="compose-h">
                  <h2 className="compose-h__t">Delivery</h2>
                  <span className="compose-h__hint">
                    {marketing
                      ? 'marketing · unsubscribe footer · auto-suppressed'
                      : 'transactional · no footer · @labcraft.dev'}
                  </span>
                </div>
              </div>
              <SenderFields
                identities={laneIdentities}
                from={from}
                onFrom={setFrom}
                replyTo={replyTo}
                onReplyTo={setReplyTo}
                defaultFrom={laneDefaultFrom}
                defaultReplyTo={senders.data?.defaultReplyTo}
              />
            </div>
            <MessageComposer
              subject={subject}
              onSubject={setSubject}
              body={body}
              onBody={setBody}
              format={format}
              onFormat={setFormat}
              footer={marketing}
              from={previewFrom}
              templates={COMPOSE_TEMPLATES}
            />
          </section>
        </div>
      )}

      <ReviewDrawer
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onComposeAnother={composeAnother}
        classification={classification}
        count={recipientCount ?? 0}
        scopeLabel={
          scope === 'selected'
            ? `${selected.size} checked recipients`
            : `${resolvedCount ?? 0} learners`
        }
        whyMatch={
          scope === 'selected'
            ? 'hand-picked learners'
            : describeFilters(filters)
        }
        subject={subject.trim()}
        body={body}
        format={format}
        bodyHtml={format === 'html' ? renderMarkdown(body) : ''}
        broad={isBroad}
        ack={ack}
        onAck={setAck}
        isPending={send.isPending}
        result={send.data ?? null}
        error={sendError}
        onSend={onSend}
      />
    </div>
  )
}
